"use client";

import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

import { getEventNavigationData } from "~/lib/gtm/getGtmEventData";
import GTMEventLink from "~/components/ui/GTMEventLink";
import useAuthActions from "~/services/auth/use-auth-actions";
import MenuItem from "./MenuItem";
import { cn } from "~/lib/utils";
import { useScrollLock } from "~/hooks/useScrollLock";
import { STORAGE_KEYS } from "~/types/Storage";
import { useLocalStorage } from "~/hooks/useLocalStorage";
import { TABS } from "~/constants/HomeTabs";
import { getStorageItem } from "~/lib/storage/Storage";
import { useRouteFlags } from "~/hooks/useRouteFlags";
import NavbarSpacer from "./NavbarSpacer";
import useUserIsAuth from "~/hooks/useIsAuth";
import { StoredCartItem } from "~/types/Checkout";
import Container from "~/componentsDS/Layout/Container";
import PromoBar from "~/components/layout/PromoBar";

import Menu from "./Menu";
import GetStartedLink from "./GetStartedLink";
import LogoIcon from "@/icons/brand/logo.svg";
import CloseBigIcon from "@/icons/actions/close-big-v2.svg";
import BurgerIcon from "@/icons/actions/burger-v2.svg";
import { UserCircleIcon } from "@phosphor-icons/react/ssr";
import CartIcon from "@/icons/ui/cart.svg";
import { APP_ROUTES } from "~/constants/appRoutes";

const MobileMenu = dynamic(() => import("./MobileMenu"), {
    ssr: false,
});

const Navbar = () => {
    const { isAuthUser } = useUserIsAuth();
    const { logOut } = useAuthActions();
    const { hideGetStartedLink, isThankYou } = useRouteFlags();
    const [isOpen, setOpen] = useState(false);
    const [isCartEnabled, setCartEnabled] = useState(false);

    useScrollLock(isOpen);

    const handleToggleMenu = () => {
        setOpen((prevState) => !prevState);
    };

    const [cartStorage] = useLocalStorage<
            StoredCartItem[] | null
        >(STORAGE_KEYS.CART_STORAGE_KEY, null);

    useEffect(() => {
        const isCompleted = Boolean(cartStorage?.length);
        const isVisited = getStorageItem(STORAGE_KEYS.CHECKOUT_VISIT_URL);

        // eslint-disable-next-line react-hooks/set-state-in-effect
        setCartEnabled(isCompleted && !!isVisited);
    }, [cartStorage]);

    const checkoutUrl = getStorageItem(STORAGE_KEYS.CHECKOUT_VISIT_URL) || APP_ROUTES.CHECKOUT_BILLING;

    return (
        <>
            <header className="!sticky -top-10 left-0 right-0 z-50 flex h-10 flex-col items-center-center duration-200">
                <PromoBar />

                <Container className="mt-3">
                    <div className="-mx-6 md:-mx-10 min-[1440px]:mx-[-30px]">
                        <div className={cn(
                            "relative flex lg:justify-between items-center h-[64px] px-4 md:px-10 2xl:px-[30px] rounded-full",
                            "bg-pureWhite shadow-[0_4px_34px_#0000000A]"
                        )}>
                            <div className={cn("flex items-center gap-3 max-lg:mr-auto")}>
                                <GTMEventLink
                                    href="/"
                                    aria-label="Inner Balance home"
                                    gtmEventData={getEventNavigationData("logo")}
                                >
                                    <LogoIcon className="block w-[133px] h-[16px] lg:w-[150px] lg:h-[18px]" />
                                </GTMEventLink>
                            </div>

                            <div className="hidden lg:flex">
                                <Menu isAuth={isAuthUser}/>
                            </div>

                            {isAuthUser ? (
                                <div className="flex items-center justify-end gap-1 2xl:min-w-[186px]">
                                    <MenuItem
                                        text=""
                                        icon={<UserCircleIcon width={32} height={32} weight="fill" color="#5472CC" />}
                                        href={`/home?tab=${TABS.PROFILE}`}
                                        gtmEventData={getEventNavigationData("Account")}
                                    />

                                    <MenuItem
                                        text="Log out"
                                        href="#"
                                        className="hidden lg:block font-suisseBP text-[16px] leading-[60px] font-medium text-heroBlue pt-[2px]"
                                        gtmEventData={getEventNavigationData("Log out")}
                                        handleClick={logOut}
                                    />
                                </div>
                            ) : (
                                <div className="flex items-center justify-end gap-4 xl:min-w-[186px] ml-2">
                                    <MenuItem
                                        text="Log in"
                                        href={APP_ROUTES.SIGN_IN}
                                        className="hidden lg:block font-suisseBP text-[16px] leading-[60px] font-normal capitalize text-trueBlack hover:text-primary400 transition-all duration-200 ease-in-out pt-[2px]"
                                        gtmEventData={getEventNavigationData("Log in")}
                                    />

                                    {isCartEnabled && !isThankYou ? (
                                        <>
                                            <MenuItem
                                                text=""
                                                icon={<CartIcon width={32} height={32} />}
                                                href={checkoutUrl}
                                                className="lg:hidden text-heroBlue text-sm font-platform font-medium leading-[64px] tracking-[1.3px]"
                                                gtmEventData={getEventNavigationData("Back to cart")}
                                            />

                                            <MenuItem
                                                text="Back to cart"
                                                icon={<CartIcon width={26} height={26} fill="#fff" />}
                                                href={checkoutUrl}
                                                className="hidden lg:inline-flex items-center justify-center gap-2 h-[40px] cursor-pointer rounded-full font-suisseBP text-[12px] leading-[20px] font-medium tracking-[1.12px] bg-heroBlue text-white px-6 pt-[2px] uppercase whitespace-nowrap"
                                                gtmEventData={getEventNavigationData("Back to cart")}
                                            />
                                        </>
                                    ) : (
                                        hideGetStartedLink ? null : <GetStartedLink />
                                    )}
                                </div>
                            )}
                            <button type="button" className="lg:hidden ml-2 cursor-pointer" onClick={handleToggleMenu}>
                                {isOpen ? <CloseBigIcon width={32} height={32}/> : <BurgerIcon width={32} height={32}/>}
                            </button>
                        </div>
                    </div>
                </Container>
            </header>

            <MobileMenu isOpen={isOpen} setOpen={setOpen} isCartEnabled={isCartEnabled} checkoutUrl={checkoutUrl} />
            <NavbarSpacer />
        </>
    );
};

export default Navbar;
